import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Download, Search, Filter, ArrowUpRight, Clock,
  CheckCircle, XCircle, AlertCircle, DollarSign,
  FileText, Calendar, ChevronDown, X, ArrowRight,
  ExternalLink,
  BanknoteIcon, Loader2, CreditCard, RefreshCw, RotateCcw
} from "lucide-react";
import { Card } from "../components/ui/card";
import SharedHeader4 from "../Headers/SharedHeader4";
import SharedFooter3 from "../Footer/SharedFooter3";
import WithdrawalReceiptDialog from "../components/payment/WithdrawalReceiptDialog";
import RetryWithdrawalDialog from "../components/payment/RetryWithdrawalDialog";
import { apiService } from "../services/paymentAPI";
import { Withdrawal, WithdrawalStatus } from "../types/payment";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getDynamicCurrencySymbol } from "../lib/currencyUtils";

interface WithdrawalHistoryData {
  totalWithdrawn: number;
  totalCompleted: number;
  totalProcessing: number;
  totalFailed: number;
  totalPending: number;
  totalCancelled: number;
  currency: string;
  withdrawals: Withdrawal[];
  totalCount: number;
  hasNextPage: boolean;
}

const WithdrawHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);

  // State management
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState<number | "all">("all");
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedWithdrawalForReceipt, setSelectedWithdrawalForReceipt] = useState<Withdrawal | null>(null);
  const [showRetryDialog, setShowRetryDialog] = useState(false);
  const [selectedWithdrawalForRetry, setSelectedWithdrawalForRetry] = useState<Withdrawal | null>(null);
  
  // Data state
  const [historyData, setHistoryData] = useState<WithdrawalHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10
  });

  // Fetch withdrawal history
  const fetchWithdrawalHistory = async () => {
    if (!user?.id) {
      setError(t("withdrawalHistory.userNotAuthenticated"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const filters: any = {};
      if (statusFilter !== "all") {
        filters.status = statusFilter;
      }

      const response = await apiService.getWithdrawalHistory(
        pagination.page,
        pagination.limit,
        filters
      );

      const data = response.data || response;
      setHistoryData(data);
    } catch (err: any) {
      console.error("Error fetching withdrawal history:", err);
      setError(err?.response?.data?.message || t("withdrawalHistory.failedToFetchHistory"));
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when pagination/filters change
  useEffect(() => {
    fetchWithdrawalHistory();
  }, [user?.id, pagination.page, pagination.limit, statusFilter]);

  // Refresh data
  const refreshData = () => {
    fetchWithdrawalHistory();
  };

  // Helper function for currency formatting
  const formatCurrency = (amount: number) => {
    const formattedAmount = amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${getDynamicCurrencySymbol()}${formattedAmount}`;
  };

  // Helper function to get status info
  const getStatusInfo = (status: number) => {
    switch (status) {
      case WithdrawalStatus.COMPLETED:
        return {
          label: t("withdrawalHistory.statusLabels.completed"),
          color: "text-green-800",
          bgColor: "bg-green-100",
          icon: CheckCircle
        };
      case WithdrawalStatus.PROCESSING:
        return {
          label: t("withdrawalHistory.statusLabels.processing"),
          color: "text-yellow-800",
          bgColor: "bg-yellow-100",
          icon: Clock
        };
      case WithdrawalStatus.FAILED:
        return {
          label: t("withdrawalHistory.statusLabels.failed"),
          color: "text-red-800",
          bgColor: "bg-red-100",
          icon: XCircle
        };
      case WithdrawalStatus.CANCELLED:
        return {
          label: t("withdrawalHistory.statusLabels.cancelled"),
          color: "text-gray-800",
          bgColor: "bg-gray-100",
          icon: XCircle
        };
      case WithdrawalStatus.PENDING:
      default:
        return {
          label: t("withdrawalHistory.statusLabels.pending"),
          color: "text-blue-800",
          bgColor: "bg-blue-100",
          icon: Clock
        };
    }
  };

  // Get withdrawal method display info
  const getMethodInfo = (withdrawal: Withdrawal) => {
    if (withdrawal.withdrawalMethod === "paypal" && withdrawal.paypalEmail) {
      return {
        type: t("withdrawalHistory.paypal"),
        name: t("withdrawalHistory.paypal"),
        detail: withdrawal.paypalEmail,
        icon: CreditCard
      };
    } else if (withdrawal.stripeTransferId) {
      return {
        type: t("withdrawalHistory.stripe"),
        name: t("withdrawalHistory.bankTransfer"),
        detail: `${t("withdrawalHistory.transferId")}: ${withdrawal.stripeTransferId.substring(0, 20)}...`,
        icon: CreditCard
      };
    } else {
      return {
        type: withdrawal.withdrawalMethod || "Stripe",
        name: t("withdrawalHistory.bankTransfer"),
        detail: t("withdrawalHistory.processingText"),
        icon: BanknoteIcon
      };
    }
  };

  // Open receipt dialog
  const openReceiptDialog = (withdrawal: Withdrawal) => {
    if (!withdrawal) {
      alert(t("withdrawalHistory.noReceiptData"));
      return;
    }
    setSelectedWithdrawalForReceipt(withdrawal);
    setShowReceiptDialog(true);
  };

  // Open retry dialog
  const openRetryDialog = (withdrawal: Withdrawal) => {
    if (!withdrawal || withdrawal.status !== WithdrawalStatus.FAILED) {
      return;
    }
    setSelectedWithdrawalForRetry(withdrawal);
    setShowRetryDialog(true);
  };

  // Handle retry withdrawal
  const handleRetryWithdrawal = async (withdrawal: Withdrawal) => {
    // Create a new withdrawal with the same details
    await apiService.createWithdrawal({
      amount: withdrawal.amount || 0,
      currency: withdrawal.currency || "USD",
      withdrawalMethod: withdrawal.withdrawalMethod || "stripe",
      paypalEmail: withdrawal.paypalEmail,
      notes: `Retry of failed withdrawal ${withdrawal.id}`,
    });

    // Refresh data and close modals
    refreshData();
    setSelectedWithdrawal(null);
    setShowRetryDialog(false);
    setSelectedWithdrawalForRetry(null);
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: number }) => {
    const statusInfo = getStatusInfo(status);
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  // Withdrawal details modal
  const WithdrawalDetailsModal = ({ withdrawal, onClose }: { withdrawal: Withdrawal | null, onClose: () => void }) => {
    if (!withdrawal) return null;

    const formatDate = (date: Date | string) => {
      return new Date(date).toLocaleString();
    };

    const statusInfo = getStatusInfo(withdrawal.status || WithdrawalStatus.PENDING);
    const StatusIcon = statusInfo.icon;
    const methodInfo = getMethodInfo(withdrawal);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full p-4 sm:p-6 md:p-8 max-h-[95vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex justify-between items-start mb-4 sm:mb-6">
            <div className="flex-1 min-w-0 pr-2">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-1">{t("withdrawalHistory.withdrawalDetails")}</h2>
              <p className="text-xs sm:text-sm text-gray-600 truncate">ID: {withdrawal.id}</p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 transition-colors"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-500" />
            </button>
          </div>

          {/* Status Badge */}
          <div className="flex items-start sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6 p-3 bg-gray-50 rounded-lg">
            <StatusIcon className={`h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0 ${statusInfo.color}`} />
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">{statusInfo.label}</h3>
              <p className="text-xs sm:text-sm text-gray-600 break-words">
                {withdrawal.status === WithdrawalStatus.COMPLETED && withdrawal.completedAt
                  ? t("withdrawalHistory.completedOn", { date: formatDate(withdrawal.completedAt) })
                  : withdrawal.status === WithdrawalStatus.PROCESSING && withdrawal.processedAt
                  ? t("withdrawalHistory.processingSince", { date: formatDate(withdrawal.processedAt) })
                  : t("withdrawalHistory.createdOn", { date: formatDate(withdrawal.createdAt) })}
              </p>
            </div>
          </div>

          {/* Amount and Payment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {/* Amount Details */}
            <div>
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-4">{t("withdrawalHistory.amountDetails")}</h3>
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg space-y-2 sm:space-y-3">
                <div className="flex justify-between items-start gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">{t("withdrawalHistory.withdrawalAmount")}</span>
                  <span className="font-medium text-sm sm:text-base text-red-600 text-right">-{formatCurrency(withdrawal.amount || 0)}</span>
                </div>
                <div className="flex justify-between items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">{t("withdrawalHistory.currency")}</span>
                  <span className="font-medium text-sm sm:text-base">{withdrawal.currency?.toUpperCase() || "USD"}</span>
                </div>
                <div className="flex justify-between items-center gap-2 pt-2 border-t">
                  <span className="font-medium text-xs sm:text-sm">{t("withdrawalHistory.status")}</span>
                  <StatusBadge status={withdrawal.status || WithdrawalStatus.PENDING} />
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-4">{t("withdrawalHistory.paymentMethod")}</h3>
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  {React.createElement(methodInfo.icon, { className: "h-4 w-4 sm:h-5 sm:w-5 text-gray-600 flex-shrink-0" })}
                  <span className="font-medium text-sm sm:text-base text-gray-900">{methodInfo.type}</span>
                </div>
                <div className="flex justify-between items-start gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">{t("withdrawalHistory.method")}</span>
                  <span className="font-medium text-xs sm:text-sm text-right">{methodInfo.name}</span>
                </div>
                <div className="flex justify-between items-start gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">{t("withdrawalHistory.details")}</span>
                  <span className="font-medium text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[200px] text-right">{methodInfo.detail}</span>
                </div>
                {withdrawal.withdrawalMethod === "stripe" && withdrawal.stripeTransferId && (
                  <div className="flex justify-between items-start gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600">{t("withdrawalHistory.transferId")}</span>
                    <span className="font-mono text-[10px] sm:text-xs truncate max-w-[100px] sm:max-w-[150px] text-right">
                      {withdrawal.stripeTransferId}
                    </span>
                  </div>
                )}
                {withdrawal.withdrawalMethod === "paypal" && withdrawal.paypalPayoutBatchId && (
                  <div className="flex justify-between items-start gap-2 text-xs sm:text-sm">
                    <span className="text-gray-600">{t("withdrawalHistory.payoutBatchId")}</span>
                    <span className="font-mono text-[10px] sm:text-xs truncate max-w-[100px] sm:max-w-[150px] text-right">
                      {withdrawal.paypalPayoutBatchId}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Timeline Section */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-4">{t("withdrawalHistory.transactionTimeline")}</h3>
            <div className="space-y-2 sm:space-y-3">
              {/* Created */}
              <div className="flex items-start gap-2 sm:gap-3">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm sm:text-base text-gray-900">{t("withdrawalHistory.withdrawalCreated")}</p>
                  <p className="text-xs sm:text-sm text-gray-500 break-words">{formatDate(withdrawal.createdAt)}</p>
                </div>
              </div>
              
              {/* Processing */}
              {withdrawal.processedAt && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900">{t("withdrawalHistory.processingStarted")}</p>
                    <p className="text-xs sm:text-sm text-gray-500 break-words">{formatDate(withdrawal.processedAt)}</p>
                  </div>
                </div>
              )}
              
              {/* Completed */}
              {withdrawal.completedAt && withdrawal.status === WithdrawalStatus.COMPLETED && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900">{t("withdrawalHistory.withdrawalCompleted")}</p>
                    <p className="text-xs sm:text-sm text-gray-500 break-words">{formatDate(withdrawal.completedAt)}</p>
                  </div>
                </div>
              )}
              
              {/* Failed */}
              {withdrawal.status === WithdrawalStatus.FAILED && (
                <div className="flex items-start gap-2 sm:gap-3">
                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900">{t("withdrawalHistory.withdrawalFailed")}</p>
                    <p className="text-xs sm:text-sm text-red-600 break-words">{withdrawal.failureReason || t("withdrawalHistory.unknownError")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          {withdrawal.notes && (
            <div className="mb-4 sm:mb-6">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">{t("withdrawalHistory.notes")}</h3>
              <div className="p-3 sm:p-4 bg-gray-50 rounded-lg">
                <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-wrap break-words">{withdrawal.notes}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
            {withdrawal.status === WithdrawalStatus.PENDING && (
              <button
                onClick={async () => {
                  const confirmCancel = window.confirm(t("withdrawalHistory.confirmCancel"));
                  if (!confirmCancel) return;
                  
                  try {
                    await apiService.cancelWithdrawal(withdrawal.id);
                    refreshData();
                    onClose();
                  } catch (err: any) {
                    alert(err?.response?.data?.message || t("withdrawalHistory.cancelFailed"));
                  }
                }}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm sm:text-base font-medium transition-colors"
              >
                {t("withdrawalHistory.cancelWithdrawal")}
              </button>
            )}
            {withdrawal.status === WithdrawalStatus.FAILED && (
              <button
                onClick={() => openRetryDialog(withdrawal)}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-2 text-sm sm:text-base font-medium transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{t("withdrawalHistory.retryWithdrawal")}</span>
              </button>
            )}
            <button
              onClick={() => {
                onClose();
                openReceiptDialog(withdrawal);
              }}
              className="px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base font-medium transition-colors"
            >
              <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span>{t("withdrawalHistory.viewReceipt")}</span>
            </button>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // Loading state
  if (loading && !historyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
        <SharedHeader4 />
        <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <span className="text-gray-600">{t("withdrawalHistory.loadingWithdrawalHistory")}</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20" style={{ textAlign: "left" }}>
        <SharedHeader4 />
        <main className="max-w-7xl mx-auto px-4 py-8 w-full">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t("withdrawalHistory.errorLoadingData")}</h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={refreshData}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 mx-auto"
              >
                <RefreshCw className="h-4 w-4" />
                {t("withdrawalHistory.tryAgain")}
              </button>
            </div>
          </div>
        </main>
        <footer className="fixed bottom-0 left-0 right-0 z-40 bg-white shadow-lg">
          <SharedFooter3 />
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20"  style={{ textAlign: "left" }}>
      <SharedHeader4 />
      
      <main className="max-w-7xl mx-auto px-4 py-8 w-full">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t("withdrawalHistory.title")}</h1>
              <p className="text-gray-600 mt-1">{t("withdrawalHistory.subtitle")}</p>
            </div>
            <button
              onClick={refreshData}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {t("withdrawalHistory.refresh")}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Download className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("withdrawalHistory.totalWithdrawn")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {formatCurrency(historyData?.totalWithdrawn || 0)}
              </span>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("withdrawalHistory.completed")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {historyData?.totalCompleted || 0}
              </span>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("withdrawalHistory.processing")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {historyData?.totalProcessing || 0}
              </span>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-500">{t("withdrawalHistory.failed")}</h3>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-gray-900">
                {historyData?.totalFailed || 0}
              </span>
            </div>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card className="mb-8">
          <div className="p-4 border-b">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 sm:flex-none sm:min-w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("withdrawalHistory.searchWithdrawals")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  <Filter className="h-5 w-5" />
                  <span>{t("withdrawalHistory.filters")}</span>
                </button>
              </div>
            </div>

            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="mt-4 pt-4 border-t"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("withdrawalHistory.status")}
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value === "all" ? "all" : Number(e.target.value))}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">{t("withdrawalHistory.allStatus")}</option>
                        <option value={WithdrawalStatus.COMPLETED}>{t("withdrawalHistory.completed")}</option>
                        <option value={WithdrawalStatus.PROCESSING}>{t("withdrawalHistory.processing")}</option>
                        <option value={WithdrawalStatus.PENDING}>{t("withdrawalHistory.pending")}</option>
                        <option value={WithdrawalStatus.FAILED}>{t("withdrawalHistory.failed")}</option>
                        <option value={WithdrawalStatus.CANCELLED}>{t("withdrawalHistory.cancelled")}</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("withdrawalHistory.resultsPerPage")}
                      </label>
                      <select
                        value={pagination.limit}
                        onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="10">{t("withdrawalHistory.perPage10")}</option>
                        <option value="25">{t("withdrawalHistory.perPage25")}</option>
                        <option value="50">{t("withdrawalHistory.perPage50")}</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        setStatusFilter("all");
                        setPagination({ page: 1, limit: 10 });
                        setShowFilters(false);
                      }}
                      className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      {t("withdrawalHistory.resetFilters")}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Withdrawals List */}
          <div className="divide-y">
            {!historyData?.withdrawals?.length ? (
              <div className="p-6 text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t("withdrawalHistory.noWithdrawalsFound")}</h3>
                <p className="text-gray-600">{t("withdrawalHistory.noWithdrawalsDesc")}</p>
              </div>
            ) : (
              historyData.withdrawals.map((withdrawal) => {
                const statusInfo = getStatusInfo(withdrawal.status || WithdrawalStatus.PENDING);
                const methodInfo = getMethodInfo(withdrawal);
                const MethodIcon = methodInfo.icon;

                return (
                  <div
                    key={withdrawal.id}
                    className="p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedWithdrawal(withdrawal)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${statusInfo.bgColor}`}>
                          <ArrowUpRight className={`h-5 w-5 ${statusInfo.color}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-medium text-red-600">
                              -{formatCurrency(withdrawal.amount || 0)}
                            </h3>
                            <StatusBadge status={withdrawal.status || WithdrawalStatus.PENDING} />
                          </div>
                          <p className="text-sm text-gray-600">
                            ID: {withdrawal.id}
                          </p>
                          <p className="text-sm text-gray-600">
                            {new Date(withdrawal.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col sm:items-end gap-1">
                        <div className="flex items-center gap-2">
                          <MethodIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {methodInfo.type}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500 truncate max-w-[200px]">
                          {methodInfo.detail}
                        </span>
                        {withdrawal.status === WithdrawalStatus.COMPLETED && withdrawal.completedAt && (
                          <span className="text-sm text-gray-500">
                            {t("withdrawalHistory.completedOnShort", { date: new Date(withdrawal.completedAt).toLocaleDateString() })}
                          </span>
                        )}
                        {withdrawal.status === WithdrawalStatus.FAILED && (
                          <>
                            {withdrawal.failureReason && (
                              <span className="text-sm text-red-600 truncate max-w-[200px]">
                                {withdrawal.failureReason}
                              </span>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openRetryDialog(withdrawal);
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                            >
                              <RotateCcw className="h-3 w-3" />
                              {t("withdrawalHistory.retryWithdrawal")}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4 flex items-center gap-4">
                      <div className="flex-1">
                        <div className="relative flex items-center">
                          {/* Pending */}
                          <div className="h-2 w-2 rounded-full bg-blue-500" />
                          <div className={`h-0.5 flex-1 ${
                            withdrawal.status && withdrawal.status >= WithdrawalStatus.PROCESSING
                              ? "bg-yellow-500"
                              : "bg-gray-200"
                          }`} />
                          
                          {/* Processing */}
                          <div className={`h-2 w-2 rounded-full ${
                            withdrawal.status && withdrawal.status >= WithdrawalStatus.PROCESSING
                              ? "bg-yellow-500"
                              : "bg-gray-300"
                          }`} />
                          <div className={`h-0.5 flex-1 ${
                            withdrawal.status === WithdrawalStatus.COMPLETED
                              ? "bg-green-500"
                              : withdrawal.status === WithdrawalStatus.FAILED
                              ? "bg-red-500"
                              : "bg-gray-200"
                          }`} />
                          
                          {/* Completed/Failed */}
                          <div className={`h-2 w-2 rounded-full ${
                            withdrawal.status === WithdrawalStatus.COMPLETED
                              ? "bg-green-500"
                              : withdrawal.status === WithdrawalStatus.FAILED
                              ? "bg-red-500"
                              : "bg-gray-300"
                          }`} />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openReceiptDialog(withdrawal);
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100"
                        >
                          <Download className="h-3 w-3" />
                          <span className="hidden sm:inline">{t("withdrawalHistory.receipt")}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedWithdrawal(withdrawal);
                          }}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                        >
                          {t("withdrawalHistory.viewDetails")}
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {historyData && historyData.withdrawals.length > 0 && (
            <div className="p-4 border-t">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {t("withdrawalHistory.showingWithdrawals", { count: historyData.withdrawals.length, total: historyData.totalCount })}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1 || loading}
                    className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("withdrawalHistory.previous")}
                  </button>
                  <span className="px-3 py-2 text-sm text-gray-600">
                    {t("withdrawalHistory.page", { page: pagination.page })}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={!historyData.hasNextPage || loading}
                    className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t("withdrawalHistory.next")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Withdrawal Details Modal */}
        <AnimatePresence>
          {selectedWithdrawal && (
            <WithdrawalDetailsModal
              withdrawal={selectedWithdrawal}
              onClose={() => setSelectedWithdrawal(null)}
            />
          )}
        </AnimatePresence>

        {/* Withdrawal Receipt Dialog */}
        <WithdrawalReceiptDialog
          isOpen={showReceiptDialog}
          onClose={() => {
            setShowReceiptDialog(false);
            setSelectedWithdrawalForReceipt(null);
          }}
          withdrawal={selectedWithdrawalForReceipt}
        />

        {/* Retry Withdrawal Dialog */}
        <RetryWithdrawalDialog
          isOpen={showRetryDialog}
          onClose={() => {
            setShowRetryDialog(false);
            setSelectedWithdrawalForRetry(null);
          }}
          withdrawal={selectedWithdrawalForRetry}
          onRetry={handleRetryWithdrawal}
        />
      </main>

      {/* Footer - Sticky to Bottom */}
      {!loading && (
        <footer className="fixed bottom-0 left-0 right-0 z-40 bg-white shadow-lg">
          <SharedFooter3/>
        </footer>
      )}
    </div>
  );
};

export default WithdrawHistory;